import {ObjectUtils} from 'primeng/utils';

import {FilterMatchStrategyType} from './filter-strategy.type';

export type FilterFn<T = any, V = any> = FnTypes.Fn<[item: T, field: keyof T, value: V], boolean>;

/**
 * @description A factory function for creating a filter function by a given strategy type
 * @param strategy - The strategy type to use
 * @constructor
 */
export const FilterFactory = (strategy: FilterMatchStrategyType): FilterFn => {
	const Filters: Record<FilterMatchStrategyType, FilterFn> = {
		contains: (item, field, value) => {
			if (value === undefined || value === null || (typeof value === 'string' && value.trim() === '')) {
				return true;
			}

			if (item === undefined || item === null) {
				return false;
			}

			const filterValue = ObjectUtils.removeAccents(value.toString()).toLocaleLowerCase();
			const stringValue = ObjectUtils.removeAccents(item[field].toString()).toLocaleLowerCase();

			return stringValue.indexOf(filterValue) !== -1;
		},
		equals: (item, field, value) => {
			if (value === undefined || value === null || (typeof value === 'string' && value.trim() === '')) {
				return true;
			}

			if (item === undefined || item === null) {
				return false;
			}

			if (item[field].getTime && value.getTime) return item[field].getTime() === value.getTime();
			else return ObjectUtils.removeAccents(item[field].toString()).toLocaleLowerCase() == ObjectUtils.removeAccents(value.toString()).toLocaleLowerCase();
		},
		lt: (item, field, value) => {
			if (value === undefined || value === null) {
				return true;
			}

			if (item === undefined || item === null) {
				return false;
			}

			if (item[field].getTime && value.getTime) return item[field].getTime() < value.getTime();
			else return item[field] < value;
		},
		lte: (item, field, value) => {
			if (value === undefined || value === null) {
				return true;
			}

			if (item === undefined || item === null) {
				return false;
			}

			if (item[field].getTime && value.getTime) return item[field].getTime() <= value.getTime();
			else return item[field] <= value;
		},
		gt: (item, field, value) => {
			if (value === undefined || value === null) {
				return true;
			}

			if (item === undefined || item === null) {
				return false;
			}

			if (item[field].getTime && value.getTime) return item[field].getTime() > value.getTime();
			else return item[field] > value;
		},

		is: (item, field, value) => {
			return Filters.equals(item, field, value);
		},
		isNot: (item, field, value) => {
			return Filters.notEquals(item, field, value);
		},
		before: (item, field, value) => {
			return Filters.lt(item, field, value);
		},
		after: (item, field, value) => {
			return Filters.gt(item, field, value);
		},
		dateIs: (item, field, value) => {
			if (value === undefined || value === null) {
				return true;
			}

			if (item === undefined || item === null) {
				return false;
			}

			return item[field].toDateString() === value.toDateString();
		},
		dateIsNot: (item, field, value) => {
			if (value === undefined || value === null) {
				return true;
			}

			if (item === undefined || item === null) {
				return false;
			}

			return item[field].toDateString() !== value.toDateString();
		},
		dateBefore: (item, field, value) => {
			if (value === undefined || value === null) {
				return true;
			}

			if (item === undefined || item === null) {
				return false;
			}

			return item[field].getTime() < value.getTime();
		},
		dateAfter: (item, field, value) => {
			if (value === undefined || value === null) {
				return true;
			}

			if (item === undefined || item === null) {
				return false;
			}

			return item[field].getTime() > value.getTime();
		},
		startsWith: (item, field, value) => {
			if (value === undefined || value === null || value.trim() === '') {
				return true;
			}

			if (item === undefined || item === null) {
				return false;
			}

			const filterValue = ObjectUtils.removeAccents(value.toString()).toLocaleLowerCase();
			const stringValue = ObjectUtils.removeAccents(item[field].toString()).toLocaleLowerCase();

			return stringValue.slice(0, filterValue.length) === filterValue;
		},
		notContains: (item, field, value) => {
			if (value === undefined || value === null || value.trim() === '') {
				return true;
			}

			if (item === undefined || item === null) {
				return false;
			}

			const filterValue = ObjectUtils.removeAccents(value.toString()).toLocaleLowerCase();
			const stringValue = ObjectUtils.removeAccents(item[field].toString()).toLocaleLowerCase();

			return stringValue.indexOf(filterValue) === -1;
		},
		endsWith: (item, field, value) => {
			if (value === undefined || value === null || value.trim() === '') {
				return true;
			}

			if (item === undefined || item === null) {
				return false;
			}

			const filterValue = ObjectUtils.removeAccents(value.toString()).toLocaleLowerCase();
			const stringValue = ObjectUtils.removeAccents(item[field].toString()).toLocaleLowerCase();

			return stringValue.indexOf(filterValue, stringValue.length - filterValue.length) !== -1;
		},
		in: (item, field, value) => {
			if (value === undefined || value === null || value.length === 0) {
				return true;
			}

			for (let i = 0; i < value.length; i++) {
				if (ObjectUtils.equals(item[field], value[i])) {
					return true;
				}
			}

			return false;
		},
		between: (item, field, value) => {
			if (value == null || value[0] == null || value[1] == null) {
				return true;
			}

			if (item === undefined || item === null) {
				return false;
			}

			if (item[field].getTime) return value[0].getTime() <= item[field].getTime() && item[field].getTime() <= value[1].getTime();
			else return value[0] <= item[field] && item[field] <= value[1];
		},
		notEquals: (item, field, value) => {
			if (value === undefined || value === null || (typeof value === 'string' && value.trim() === '')) {
				return false;
			}

			if (item === undefined || item === null) {
				return true;
			}

			if (item[field].getTime && value.getTime) return item[field].getTime() !== value.getTime();
			else return ObjectUtils.removeAccents(item[field].toString()).toLocaleLowerCase() != ObjectUtils.removeAccents(value.toString()).toLocaleLowerCase();
		},
	};
	return Filters[strategy];
};
